import { useEffect, useState } from 'react';
import { CreditCard, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { etiquetaMarcaTarjeta } from '@/lib/openpay';
import { Panel } from '@/components/ui';
import type { OpenpayTarjeta } from '@/types';

export function TarjetasGuardadas() {
  const [tarjetas, setTarjetas] = useState<OpenpayTarjeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eliminando, setEliminando] = useState<string | null>(null);

  const cargar = () => {
    setLoading(true);
    setError(null);
    api
      .openpayCards()
      .then(setTarjetas)
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar las tarjetas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargar();
  }, []);

  const eliminar = async (card: OpenpayTarjeta) => {
    const ok = window.confirm(
      `¿Eliminar ${etiquetaMarcaTarjeta(card.marca)} terminada en ${card.terminacion}?`,
    );
    if (!ok) return;
    setEliminando(card.id);
    setError(null);
    try {
      await api.openpayEliminarCard(card.id);
      setTarjetas((prev) => prev.filter((t) => t.id !== card.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar la tarjeta');
    } finally {
      setEliminando(null);
    }
  };

  return (
    <Panel title="Tarjetas guardadas" icon={<CreditCard className="h-4 w-4 text-brand-600" />}>
      {loading ? (
        <p className="text-sm text-surface-500">Cargando tarjetas...</p>
      ) : tarjetas.length === 0 ? (
        <p className="text-sm text-surface-500">
          Aún no tienes tarjetas guardadas. En el checkout puedes marcar{' '}
          <strong>Guardar esta tarjeta para futuras compras</strong>.
        </p>
      ) : (
        <ul className="space-y-2">
          {tarjetas.map((t) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-surface-200 px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium text-surface-800">
                  {etiquetaMarcaTarjeta(t.marca)} •••• {t.terminacion}
                </p>
                <p className="text-xs text-surface-500">
                  Vence {t.mes}/{t.anio}
                  {t.titular ? ` · ${t.titular}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="btn-ghost text-red-700"
                disabled={eliminando === t.id}
                onClick={() => eliminar(t)}
                aria-label="Eliminar tarjeta"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
    </Panel>
  );
}
